import type { User } from '../types';

// Configure API base URL based on environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:8001' : 'https://api.zeedaboa.com');

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface AuthConfig {
  google_client_id: string;
  auth_enabled: boolean;
  supports_password_auth?: boolean;
}

class AuthService {
  private token: string | null = null;
  private user: User | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('auth_token');
    const userStr = localStorage.getItem('auth_user');
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse stored user data:', e);
        localStorage.removeItem('auth_user');
      }
    }
  }

  async getAuthConfig(): Promise<AuthConfig> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/config`);
      if (!response.ok) {
        console.warn('Auth config endpoint not available, authentication disabled');
        return { google_client_id: '', auth_enabled: false };
      }
      return response.json();
    } catch (error) {
      console.warn('Failed to get auth config, authentication disabled:', error);
      return { google_client_id: '', auth_enabled: false };
    }
  }

  async loginWithGoogle(googleToken: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: googleToken,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const authResponse: AuthResponse = await response.json();
    
    // Store token and user data
    this.token = authResponse.access_token;
    this.user = authResponse.user;
    localStorage.setItem('auth_token', this.token);
    localStorage.setItem('auth_user', JSON.stringify(this.user));

    return authResponse;
  }

  async register(userData: { username: string; email: string; name: string; password: string }): Promise<AuthResponse> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
    } catch {
      throw new Error('Unable to reach the server. Please try again.');
    }

    if (!response.ok) {
      let message = 'Registration failed';
      try {
        const error = await response.json();
        // Pydantic 422 errors return detail as an array of objects
        if (Array.isArray(error.detail)) {
          message = error.detail.map((e: { msg: string }) => e.msg.replace(/^Value error, /, '')).join('. ');
        } else {
          message = error.detail || message;
        }
      } catch {
        // Response may not be JSON
      }
      throw new Error(message);
    }

    const authResponse: AuthResponse = await response.json();
    
    // Store token and user data
    this.token = authResponse.access_token;
    this.user = authResponse.user;
    localStorage.setItem('auth_token', this.token);
    localStorage.setItem('auth_user', JSON.stringify(this.user));

    return authResponse;
  }

  async loginWithPassword(username: string, password: string): Promise<AuthResponse> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
    } catch {
      throw new Error('Unable to reach the server. Please try again.');
    }

    if (!response.ok) {
      let message = 'Incorrect username or password';
      try {
        const error = await response.json();
        message = error.detail || message;
      } catch {
        // Response may not be JSON
      }
      throw new Error(message);
    }

    const authResponse: AuthResponse = await response.json();

    // Store token and user data
    this.token = authResponse.access_token;
    this.user = authResponse.user;
    localStorage.setItem('auth_token', this.token);
    localStorage.setItem('auth_user', JSON.stringify(this.user));

    // Send timezone to backend after password login
    try {
      await this.updateUserSettings({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } catch {
      // Non-critical — timezone update can fail silently
    }

    return authResponse;
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.token) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid, clear it
          this.logout();
          return null;
        }
        throw new Error('Failed to get current user');
      }

      const user: User = await response.json();
      this.user = user;
      localStorage.setItem('auth_user', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  logout(serverInvalidate: boolean = false): void {
    // Invalidate token server-side (best-effort, fire-and-forget)
    if (serverInvalidate && this.token) {
      fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.token}` },
      }).catch(() => {});
    }
    this.token = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.token && !!this.user;
  }

  async updateUserSettings(userData: { name?: string; preferences?: unknown; timezone?: string }): Promise<User> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid, clear it
          this.logout();
          throw new Error('Authentication expired');
        }
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update user settings');
      }

      const updatedUser: User = await response.json();
      this.user = updatedUser;
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }
  }

  async getWindowsConfig(device: string = 'desktop'): Promise<Record<string, unknown> | null> {
    if (!this.token) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/auth/windows-config?device=${device}`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.config ?? null;
    } catch {
      return null;
    }
  }

  async saveWindowsConfig(config: Record<string, unknown>, device: string = 'desktop'): Promise<void> {
    if (!this.token) return;
    try {
      await fetch(`${API_BASE_URL}/auth/windows-config?device=${device}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ config }),
      });
    } catch {
      // Non-critical — server save can fail silently, localStorage is the fallback
    }
  }

  async resetWindowsConfig(device: string = 'desktop'): Promise<void> {
    if (!this.token) return;
    try {
      await fetch(`${API_BASE_URL}/auth/windows-config?device=${device}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.token}` },
      });
    } catch {
      // Non-critical
    }
  }

  // Helper method to add auth headers to requests
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }
}

export const authService = new AuthService();
